import { useState, useEffect, useMemo } from 'react';
import { useGetUsersQuery } from '../../../store/services/user-service';
import { useGetDataByUserIdQuery } from '../../../store/services/report.service';

export default function useUserSearch(tenancyId) {
    const [searchQuery, setSearchQuery] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState("");
    // ADD: Date range state - defaulting to 6 months
    const [dateRange, setDateRange] = useState('6months');

    // ADD: Calculate actual dates based on selection
    const dateParams = useMemo(() => {
        const now = new Date();
        let startDate, endDate;
        switch (dateRange) {
            case '6months':
            default: // Make 6 months the fallback default
                startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                endDate = now;
                break;
            case '12months':
                startDate = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
                endDate = now;
                break;
        }
        return {
            startDate: startDate.toISOString().split('T')[0], // Format: YYYY-MM-DD
            endDate: endDate.toISOString().split('T')[0]
        };
    }, [dateRange]);

    const {
        data: usersData,
        isLoading: usersLoading
    } = useGetUsersQuery({
        perPage: 10,
        page: 0,
        includes: "",
        query: "",
        searchTerm: searchTerm,
        tenancyId: tenancyId,
        adminFilter: false,
        extendedResults: true
    }, { skip: !searchTerm || !tenancyId });

    const {
        data: userData,
        isLoading: userDataLoading,
        refetch: refetchUserData // GET THE REFETCH FUNCTION
    } = useGetDataByUserIdQuery({
        tenancyId,
        userId: selectedUserId,
        ...dateParams
    }, { skip: !selectedUserId || !tenancyId });

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery && searchQuery.trim().length > 1) {
                setSearchTerm(searchQuery.trim());
            } else {
                setFilteredUsers([]);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Update filtered users when search results arrive
    useEffect(() => {
        if (usersData && usersData.data) {
            const transformedUsers = usersData.data.map(user => ({
                id: user.id,
                name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                email: user.email
            }));
            setFilteredUsers(transformedUsers);
        }
    }, [usersData]);

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setAnchorEl(event.currentTarget);
        if (event.target.value.trim() === "") {
            setFilteredUsers([]);
        }
    };

    const handleSelectUser = (user) => {
        setSearchQuery(`${user.name} (${user.email})`);
        setSelectedUserId(user.id);
        setFilteredUsers([]);
        setAnchorEl(null);
    };

    const handleClickAway = () => {
        setFilteredUsers([]);
        setAnchorEl(null);
    };

    return {
        searchQuery,
        anchorEl,
        setAnchorEl,
        filteredUsers,
        isSearching: usersLoading,
        userData,
        userDataLoading,
        selectedUserId,
        handleSearchChange,
        handleSelectUser,
        handleClickAway,
        dateRange,
        setDateRange,
        refetchUserData
    };
}